import React, { useMemo } from "react";
import { Tooltip } from "@mui/material";

import { Download, RemoveRedEye, Send } from "@mui/icons-material";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import {
  setGeneratedLetterFilePath,
  setIsDownloading,
  setIsFileLoading,
  setShowPreview,
  setShowPreviewMode,
  setIsSendingLetter,
  setFile,
  setDownloadingModalText,
  setIsSendingSuccess,
  setNoShowLetterPreview,
} from "app/store/patientProfile/documents/slice/patientProfileDocumentSlice";

import { downloadFile, getFileBlobUrl } from "app/utils/file";
import { showErrorToast, showSuccessToast } from "utils/toast";

import { fetchPatientDocument } from "app/services/transmission/patient";
import { useSendLetterToPatientMutation } from "app/store/patient/api/patientApi";
import {
  POST_GRID_PRINT_MAIL_API_STATUS_DESCRIPTION,
  USER_ROLES,
} from "static/constants";
import useCheckSaasAccountUser from "hooks/useCheckSaasAccountUser";

const useDocumentsTableColumn = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.transmission);
  const { currentUser } = useSelector((state) => state.auth);
  const [sendLetterToPatient] = useSendLetterToPatientMutation();
  const isSaasAccountUser = useCheckSaasAccountUser();

  const sendLetterValidRoles = [
    USER_ROLES.USER,
    USER_ROLES.ADMIN,
    USER_ROLES.ASSISTANT,
  ];

  const handleFileDownload = async (rowData) => {
    try {
      dispatch(setIsDownloading(true));

      const { filePath } = rowData;
      const response = await fetchPatientDocument(filePath);

      if (response.error) {
        throw new Error(response.error);
      }

      downloadFile(response);
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      dispatch(setIsDownloading(false));
    }
  };

  const handleFilePreview = async (
    rowData,
    letterFilePath,
    isNoShowLetter = false
  ) => {
    try {
      dispatch(setIsFileLoading(true));
      dispatch(setShowPreview(true));
      dispatch(setNoShowLetterPreview(isNoShowLetter));

      const filePath = rowData ? rowData.filePath : letterFilePath;
      dispatch(setGeneratedLetterFilePath(filePath));

      const response = await fetchPatientDocument(filePath);
      if (response.error) {
        throw new Error(response.error);
      }

      const fileURL = getFileBlobUrl(response);

      dispatch(
        setFile({ url: fileURL, type: response.headers["content-type"] })
      );
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      dispatch(setIsFileLoading(false));
    }
  };

  const handleSendLetters = async (rowData) => {
    try {
      dispatch(setDownloadingModalText("Sending Letter..."));
      dispatch(setIsSendingLetter(true));

      const { filePath, _id } = rowData;

      const response = await sendLetterToPatient({
        serialNumber: profile?.serial_number,
        documentId: _id,
        filePath,
      }).unwrap();

      if (response.error) {
        throw new Error(response.error);
      }

      showSuccessToast(response?.message);
      dispatch(setIsSendingSuccess(true));
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      // dispatch(setIsSendingSuccess(false));
      dispatch(setIsSendingLetter(false));
    }
  };

  const documentsTableColumn = useMemo(
    () => [
      {
        header: "Document Name",
        accessorKey: "fileName",
        size: 250,
        filterVariant: "text",
        Cell: ({ row }) => {
          const rowData = row?.original;

          return (
            <div
              className="upload-model__table-data-file"
              onClick={() => {
                if (rowData?.fileType === "Email") {
                  return;
                }
                dispatch(setShowPreviewMode(true));
                handleFilePreview(rowData);
              }}
            >
              <Tooltip
                style={{ fontSize: "18px", width: "300px" }}
                title={rowData?.fileName}
                placement="bottom"
              >
                <div
                  style={{
                    width: "300px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rowData?.fileName || "—"}
                </div>
              </Tooltip>
            </div>
          );
        },
      },
      {
        header: "File Type",
        accessorKey: "fileType",
        cell: (info) => info.getValue(),
        enableSorting: false,
        filterFn: "includesString",
        Cell: ({ row }) => {
          const fileType = row?.original?.fileType;
          const formattedFileType = fileType?.toUpperCase();

          return (
            <Tooltip
              style={{ fontSize: "18px", width: "300px" }}
              title={formattedFileType}
              placement="bottom"
            >
              <div
                style={{
                  width: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileType ? formattedFileType : "—"}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "File Size",
        accessorKey: "fileSize",
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const fileSize = rowData.fileSize;

          return (
            <Tooltip
              style={{ fontSize: "18px", width: "300px" }}
              title={fileSize}
              placement="bottom"
            >
              <div
                style={{
                  width: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileSize || "—"}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        Cell: ({ row }) => {
          const rowData = row?.original;
          const fileType = rowData?.fileType;

          return (
            <Tooltip
              style={{ fontSize: "18px", width: "300px" }}
              title={
                fileType === "pdf"
                  ? POST_GRID_PRINT_MAIL_API_STATUS_DESCRIPTION[
                      rowData?.status
                    ] || "—"
                  : fileType === "Email"
                    ? rowData?.status
                    : "Not Applicable"
              }
              placement="bottom"
            >
              <div
                style={{
                  width: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {rowData?.fileType !== "pdf" && rowData?.fileType !== "Email"
                  ? "Not Applicable"
                  : rowData?.status || "—"}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Uploaded On",
        accessorKey: "created_at",
        filterVariant: "text",
        Cell: ({ row }) => {
          const rowData = row?.original;
          const formattedDate = moment(rowData?.created_at).format(
            "MM/DD/YYYY"
          );
          return (
            <Tooltip
              style={{ fontSize: "18px", width: "300px" }}
              title={formattedDate}
              placement="bottom"
            >
              <div
                style={{
                  width: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {rowData?.created_at ? formattedDate : "—"}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Action",
        accessorKey: "_id",
        size: 100,
        enableSorting: false,
        accessorFn: (rowData) => {
          return (
            <>
              {rowData?.fileType !== "Email" ? (
                <>
                  <Tooltip
                    style={{ fontSize: "18px", width: "300px" }}
                    title="View file"
                    placement="bottom"
                  >
                    <span
                      style={{ cursor: "pointer", marginRight: "4px" }}
                      onClick={() => {
                        dispatch(setShowPreviewMode(true));
                        handleFilePreview(rowData);
                      }}
                    >
                      <RemoveRedEye className="action-icons" />
                    </span>
                  </Tooltip>
                  <Tooltip
                    style={{ fontSize: "18px", width: "300px" }}
                    title="Download file"
                    placement="bottom"
                  >
                    <span
                      style={{ cursor: "pointer", marginRight: "4px" }}
                      onClick={() => handleFileDownload(rowData)}
                    >
                      <Download className="action-icons" />
                    </span>
                  </Tooltip>
                  {(sendLetterValidRoles.includes(currentUser.role) ||
                    !isSaasAccountUser) && (
                    <Tooltip
                      style={{ fontSize: "18px", width: "300px" }}
                      title="Send Letter"
                      placement="bottom"
                    >
                      <span
                        style={{
                          cursor:
                            !rowData?.status &&
                            rowData?.fileType === "pdf" &&
                            "pointer",
                          opacity:
                            (rowData?.status || rowData?.fileType !== "pdf") &&
                            0.3,
                        }}
                        onClick={() =>
                          !rowData?.status &&
                          rowData?.fileType === "pdf" &&
                          handleSendLetters(rowData)
                        }
                      >
                        <Send className="action-icons" />
                      </span>
                    </Tooltip>
                  )}
                </>
              ) : (
                "—"
              )}
            </>
          );
        },
      },
    ],
    []
  );

  return {
    documentsTableColumn,
    handleFilePreview,
    handleFileDownload,
  };
};

export default useDocumentsTableColumn;
